import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const MARGIN = 40;
const FOOTER_HEIGHT = 26;
const HEADER_GAP = 18;
// Retina-ish capture for crisp print output - the dominant cost of html2canvas is the one-time
// document clone (roughly constant), not this multiplier, so there's no real speed reason to
// go lower.
const CAPTURE_SCALE = 2;
// JPEG rather than PNG - this content has no transparency (html2canvas paints a solid background)
// and PNG was producing 20MB+ files for a handful of pages at retina scale.
const JPEG_QUALITY = 0.92;

async function captureElement(el) {
  // html2canvas's internal clone defaults to the CURRENT viewport size, not the full page - past
  // that height, content silently fails to render (this is why only the first, near-the-top block
  // used to capture correctly). Sizing the clone to the full document and canceling out the
  // current scroll position is the standard fix for capturing anything taller than one screen.
  return html2canvas(el, {
    scale: CAPTURE_SCALE,
    useCORS: true,
    backgroundColor: "#ffffff",
    scrollX: 0,
    scrollY: -window.scrollY,
    windowWidth: document.documentElement.scrollWidth,
    windowHeight: document.documentElement.scrollHeight,
  });
}

// A static report has no hover/click affordance, so interactive-only chrome - info tooltips,
// search boxes, filter pills, metric dropdowns (marked with data-report-hide in the source
// components, plus any native <select> since those also render OS chrome html2canvas can't
// reproduce) - is stripped for the capture and restored right after. Un-clipping the grouped-cards
// grid (fixed max-height + internal scrollbar on screen) also happens here so the whole group list
// is captured, not just whatever's currently scrolled into view.
function prepareDomForCapture(rootEl, groupedGridSelector) {
  const restoreFns = [];

  if (groupedGridSelector) {
    rootEl.querySelectorAll(groupedGridSelector).forEach((el) => {
      const prevMaxHeight = el.style.maxHeight;
      const prevOverflowY = el.style.overflowY;
      el.style.maxHeight = "none";
      el.style.overflowY = "visible";
      restoreFns.push(() => {
        el.style.maxHeight = prevMaxHeight;
        el.style.overflowY = prevOverflowY;
      });
    });
  }

  rootEl.querySelectorAll('[data-report-hide], select').forEach((el) => {
    const prevDisplay = el.style.display;
    el.style.display = "none";
    restoreFns.push(() => {
      el.style.display = prevDisplay;
    });
  });

  // html2canvas doesn't reliably honor flexbox's align-items: center for a single line of text -
  // the pill badges (trendBadge) render with their text stuck near the top of the pill regardless
  // of line-height. Switching to the pre-flexbox centering trick (line-height pinned to the pill's
  // own measured height, no flex, no vertical padding) sidesteps flex layout entirely so it centers
  // correctly however html2canvas ends up rendering it.
  rootEl.querySelectorAll('[class*="trendBadge"]').forEach((el) => {
    const rect = el.getBoundingClientRect();
    const prev = {
      display: el.style.display,
      lineHeight: el.style.lineHeight,
      paddingTop: el.style.paddingTop,
      paddingBottom: el.style.paddingBottom,
    };
    //el.style.display = "inline-block";

    el.style.paddingTop = "0";
    el.style.paddingBottom = "10px";
    restoreFns.push(() => {
      el.style.display = prev.display;
      el.style.lineHeight = prev.lineHeight;
      el.style.paddingTop = prev.paddingTop;
      el.style.paddingBottom = prev.paddingBottom;
    });
  });

  return () => restoreFns.forEach((fn) => fn());
}

// Un-clipping/hiding elements above triggers a layout reflow, and chart libraries (Chart.js,
// Recharts) resize/redraw off a ResizeObserver callback that doesn't necessarily complete within
// a rAF or two - without this, cards further down the (just-unclipped) grid can get captured
// before their charts have redrawn, leaving them blank.
function settle() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(resolve, 300)));
  });
}

// One indivisible unit that must never be cut across a page break - either a whole top-level
// section (a KPI row, a chart panel row) or, inside the grouped-cards section, one visual row of
// cards. Coordinates are CSS px relative to rootEl's top edge.
function computeBlocks(rootEl, sectionSelector, groupedCardSelector) {
  const rootTop = rootEl.getBoundingClientRect().top;
  const sections = Array.from(rootEl.querySelectorAll(sectionSelector));
  const blocks = [];

  sections.forEach((sectionEl) => {
    const isGroupedSection = sectionEl.dataset.reportSection === "grouped";
    const cards = isGroupedSection ? Array.from(sectionEl.querySelectorAll(groupedCardSelector)) : [];

    if (!isGroupedSection || cards.length === 0) {
      const r = sectionEl.getBoundingClientRect();
      blocks.push({ top: r.top - rootTop, bottom: r.bottom - rootTop });
      return;
    }

    // Group cards into their visual rows (a CSS grid wraps them) - every card in a row shares the
    // same top offset, so a break between rows never lands inside a card.
    const rows = [];
    cards.forEach((card) => {
      const r = card.getBoundingClientRect();
      const top = r.top - rootTop;
      const bottom = r.bottom - rootTop;
      const row = rows.find((existing) => Math.abs(existing.top - top) < 4);
      if (row) row.bottom = Math.max(row.bottom, bottom);
      else rows.push({ top, bottom });
    });
    rows.sort((a, b) => a.top - b.top);

    const sectionRect = sectionEl.getBoundingClientRect();
    rows.forEach((row, i) => {
      // The panel header (title/legend) sits above the first card row - fold it into that row's
      // block instead of leaving it stranded alone at a page boundary. Each row gets its own page
      // (breakAlone) rather than being packed with neighbors - these cards carry the densest mix
      // of charts/canvases in the report, so isolating them one row per page is the safest way to
      // guarantee they render in full.
      blocks.push({
        top: i === 0 ? sectionRect.top - rootTop : row.top,
        bottom: row.bottom,
        breakAlone: true,
      });
    });
  });

  return blocks.sort((a, b) => a.top - b.top);
}

function cropCanvas(sourceCanvas, sy, sh) {
  const out = document.createElement("canvas");
  out.width = sourceCanvas.width;
  out.height = Math.max(1, Math.round(sh));
  out.getContext("2d").drawImage(sourceCanvas, 0, sy, sourceCanvas.width, sh, 0, 0, sourceCanvas.width, sh);
  return out;
}

function drawFooter(pdf, pageNum, totalPages, pageWidth, pageHeight) {
  const lineY = pageHeight - FOOTER_HEIGHT;
  pdf.setDrawColor(224, 224, 224);
  pdf.setLineWidth(0.75);
  pdf.line(MARGIN, lineY, pageWidth - MARGIN, lineY);

  pdf.setFont(undefined, "normal");
  pdf.setFontSize(8.5);
  pdf.setTextColor(140, 140, 140);
  pdf.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, lineY + 14, { align: "center" });
}

// Captures `rootEl` once (the dominant cost is html2canvas's one-time document clone, so several
// smaller captures would be far slower than one big one) and paginates the result onto
// landscape legal pages, breaking only between blocks - a KPI row, a chart panel, or one row of
// grouped cards - never through the middle of one. `headerEl`, if given, is captured separately
// (it's small - real branded header markup, not part of the dashboard) and stamped onto every page.
export async function generatePortfolioDashboardReportPdf({
  rootEl,
  headerEl,
  sectionSelector,
  groupedGridSelector,
  groupedCardSelector,
}) {
  const restore = prepareDomForCapture(rootEl, groupedGridSelector);
  try {
    await settle();

    const blocks = computeBlocks(rootEl, sectionSelector, groupedCardSelector);
    if (!blocks.length) return null;

    const headerCanvas = headerEl ? await captureElement(headerEl) : null;
    const canvas = await captureElement(rootEl);
    const rootRect = rootEl.getBoundingClientRect();
    const canvasScale = canvas.width / rootRect.width;

    const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "legal" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const contentWidth = pageWidth - MARGIN * 2;
    const pxToPt = contentWidth / canvas.width;

    let headerImgData = null;
    let headerHeightPt = 0;
    if (headerCanvas) {
      headerImgData = headerCanvas.toDataURL("image/jpeg", JPEG_QUALITY);
      headerHeightPt = (headerCanvas.height / headerCanvas.width) * contentWidth;
    }

    const contentTop = MARGIN + headerHeightPt + (headerCanvas ? HEADER_GAP : 0);
    const contentBottom = pageHeight - MARGIN - FOOTER_HEIGHT;
    const maxContentHeightPt = contentBottom - contentTop;

    const flush = (fromIdx, toIdx, needsNewPage) => {
      const topCss = blocks[fromIdx].top;
      const bottomCss = blocks[toIdx].bottom;
      const sy = topCss * canvasScale;
      const sh = (bottomCss - topCss) * canvasScale;
      const heightPt = sh * pxToPt;

      if (needsNewPage) pdf.addPage();
      if (headerImgData) pdf.addImage(headerImgData, "JPEG", MARGIN, MARGIN, contentWidth, headerHeightPt);

      const cropped = cropCanvas(canvas, sy, sh);
      pdf.addImage(cropped.toDataURL("image/jpeg", JPEG_QUALITY), "JPEG", MARGIN, contentTop, contentWidth, heightPt);
    };

    // Partition into page-groups first: a breakAlone block (one grouped-cards row) always gets a
    // page to itself: the greedy packing below never reaches across one to absorb its neighbors.
    const pageGroups = [];
    let i = 0;
    while (i < blocks.length) {
      if (blocks[i].breakAlone) {
        pageGroups.push([i, i]);
        i += 1;
        continue;
      }
      let j = i;
      while (j + 1 < blocks.length && !blocks[j + 1].breakAlone) {
        const candidateHeightPt = (blocks[j + 1].bottom - blocks[i].top) * canvasScale * pxToPt;
        if (candidateHeightPt <= maxContentHeightPt) j += 1;
        else break;
      }
      pageGroups.push([i, j]);
      i = j + 1;
    }

    pageGroups.forEach(([from, to], idx) => flush(from, to, idx > 0));

    const totalPages = pdf.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      pdf.setPage(p);
      drawFooter(pdf, p, totalPages, pageWidth, pageHeight);
    }

    return pdf;
  } finally {
    restore();
  }
}
